import Modal from 'bootstrap/js/src/modal'
import * as ko from 'knockout'
import { observable, unwrap } from 'knockout-decorators'

import { Analytics, ErrorReporter } from '~/utils'
import { PaymentStatus } from '@tixa/schema'

import './payment-checker.less'

import ErrorIcon from '~/images/icons/cart_sad.svg?raw'
import TicketImage from '~/images/icons/cart_ticket.svg?raw'

import source from './payment-checker.html?raw'
import { pageState } from '~/store/page'
import { useOrderStore } from '../cart/state/store-order'

import type { ModalOptions } from '../cart/modal'
import '../cart/modal'
import '../page-footer/hubspot-form'

declare const EMBED: boolean

export class PaymentChecker {
  private orderStore = useOrderStore()
  private Tracker = Analytics.getInstance()
  private paymentStarted = false
  private paymentModal: Modal = null
  private paymentTimeout: ReturnType<typeof setTimeout> | null = null

  private ticketimage = TicketImage
  private errorIcon = ErrorIcon

  // PAYMENT
  @observable private paymentFeedback = ''
  @observable private isPaymentWaiting = false
  @observable private isPaymentSuccess = false
  @observable private isPaymentFailure = false
  @observable private transactionDate = ''
  @observable private transactionOrderId = ''
  @observable private transactionPaymentId = ''
  @observable private transactionPaymentMode = ''

  // Newsletter modal
  @observable isNewsletterModalShown = false
  private newsletterModalContainer: HTMLElement | null = null

  private modalSettings = {
    backdrop: 'static',
    show: false,
    focus: true,
    keyboard: false,
  }

  get newsletterModalOptions(): ModalOptions {
    return {
      titleKey: 'newsletter',
      bsModalSettings: this.modalSettings,
      show: unwrap<boolean>(this, 'isNewsletterModalShown'),
      size: 'lg',
      body: {
        name: 'hubspot-form-content',
      },
      hidden: () => {
        this.isNewsletterModalShown = false
      },
    }
  }

  openNewsletterModal = () => {
    this.isNewsletterModalShown = true
  }

  constructor() {
    if (document.getElementsByTagName('payment-checker').length === 0) {
      const el = document.createElement('payment-checker')
      el.classList.add('unblock')
      el.innerHTML = source
      document.body.appendChild(el)
      ko.applyBindings(this, el)

      this.newsletterModalContainer = document.createElement('div')
      this.newsletterModalContainer.id = 'payment-newsletter-modal-container'
      document.body.appendChild(this.newsletterModalContainer)

      const newsletterTemplate = `<!-- ko component: { name: "bs-modal", params: newsletterModalOptions } --><!-- /ko -->`
      this.newsletterModalContainer.innerHTML = newsletterTemplate
      ko.applyBindings(this, this.newsletterModalContainer)

      const modalElRq = el.getElementsByClassName('modal')
      if (modalElRq.length > 0) {
        const modalEl = modalElRq[0]
        this.paymentModal = new Modal(modalEl, {
          show: true,
          focus: true,
          keyboard: false,
          backdrop: 'static',
        })
        modalEl.addEventListener('hidden.bs.modal', () => {
          document.body.classList.remove('payment-open')
          window.parent.postMessage({ action: 'setModal', open: false }, '*')
        })
      }
    }
  }

  public async checkPayment(orderId: string) {
    this.isPaymentWaiting = true
    this.paymentModal.show()
    let errorCount = 0
    this.paymentStarted = true

    const storedOrder = await this.orderStore.getOrderData()
    const orderedItems = storedOrder.offers

    if (orderedItems) {
      document.body.classList.add('payment-open')
      window.parent.postMessage({ action: 'setModal', open: true }, '*')
      const doCheck = (order: string) => {
        if (this.paymentTimeout) {
          clearTimeout(this.paymentTimeout)
        }
        if (!this.isPaymentWaiting) {
          return
        }
        pageState.api.order
          .checkPayment(orderId)
          .then(async (value) => {
            if (value.status === PaymentStatus.pending) {
              this.paymentTimeout = setTimeout(() => doCheck(order), 2000)
            } else if (value.status === PaymentStatus.success) {
              if (this.Tracker && this.Tracker.purchased) {
                this.Tracker.purchased(
                  storedOrder.orderId,
                  orderedItems,
                  storedOrder.cartId,
                  storedOrder
                )
              }
              this.isPaymentSuccess = true
              this.isPaymentWaiting = false
              await this.orderStore.clear()
              try {
                if (!EMBED) {
                  await pageState.api.impression.submit({
                    action: 'completed',
                    id: orderId,
                    type: 'purchase',
                  })
                }
              } catch (error) {
                ErrorReporter.exception(error as Error)
              }
            } else if (value.status === PaymentStatus.failure) {
              if (this.Tracker && this.Tracker.paymentFailure) {
                this.Tracker.paymentFailure()
              }
              if (value.reason) {
                this.paymentFeedback = value.reason
              }
              this.isPaymentWaiting = false
              this.isPaymentFailure = true
              await this.orderStore.clear()
              try {
                if (!EMBED) {
                  await pageState.api.impression.submit({
                    action: 'failure',
                    id: orderId,
                    type: 'purchase',
                  })
                }
              } catch (error) {
                ErrorReporter.exception(error as Error)
              }
            }
            if (value.transaction) {
              this.transactionDate = value.transaction.date
              this.transactionOrderId = value.transaction.orderId
              this.transactionPaymentId = value.transaction.paymentId
              this.transactionPaymentMode = value.transaction.paymentMode
            }
          })
          .catch((error) => {
            ErrorReporter.exception(error)
            errorCount = errorCount + 1
            if (errorCount < 10) {
              this.paymentTimeout = setTimeout(() => doCheck(order), 2000)
            } else {
              this.paymentFeedback = error.response.status
              this.isPaymentWaiting = false
              this.isPaymentFailure = true
            }
          })
      }
      doCheck(orderId)
    }
  }
}
